
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { port } from '../../App';
import BackButton from '../../Components/MiniComponent/BackButton';

const LeadActivityLogPage = () => {
    const { activityId } = useParams();
    const navigate = useNavigate();
    const [leadData, setLeadData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleChatHistoryClick = () => {
        if (leadData?.lead_details?.phone) {
            localStorage.setItem('pendingInboxPhone', leadData.lead_details.phone);
            localStorage.setItem('pendingInboxName', leadData.lead_details.name || 'Unknown');
            navigate('/whatsapp/inbox');
        }
    };

    useEffect(() => {
        const fetchLog = async () => {
            try {
                // Remove trailing slash from port if present
                const basePort = port.endsWith('/') ? port.slice(0, -1) : port;
                const response = await axios.get(`${basePort}/root/activity/lead-log/${activityId}`);
                setLeadData(response.data);
            } catch (err) {
                console.error("Error fetching lead log:", err);
                setError("Failed to load lead history.");
            } finally {
                setIsLoading(false);
            }
        };

        if (activityId) {
            fetchLog();
        }
    }, [activityId]);

    const formatTimeTo12Hour = (time24) => {
        if (!time24) return '-';
        const [hours, minutes] = time24.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error || !leadData) {
        return (
            <div className="container-fluid p-4">
                <BackButton />
                <div className="alert alert-danger mt-4">{error || "No data found"}</div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-4 poppins bg-gray-50 min-h-screen">
            <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <BackButton />
                    <h2 className="text-2xl font-bold text-gray-800 m-0">Lead Activity Log</h2>
                </div>
                {leadData?.lead_details?.phone && (
                    <button
                        onClick={handleChatHistoryClick}
                        className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20ba59] text-white font-semibold px-4 py-2 rounded-xl shadow-sm transition-all"
                    >
                        <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.6.95 3.197 1.45 4.817 1.453 5.461 0 9.905-4.382 9.909-9.768.002-2.609-1.012-5.062-2.857-6.912C16.618 2.078 14.164.842 11.99.842c-5.468 0-9.911 4.384-9.915 9.773-.001 1.914.499 3.779 1.447 5.419L2.52 21.436l4.127-1.282zM17.41 14.28c-.296-.147-1.748-.85-2.016-.948-.268-.099-.463-.147-.659.147-.196.294-.756.948-.927 1.144-.171.196-.341.22-.638.073-.296-.147-1.252-.454-2.385-1.45-.882-.775-1.478-1.733-1.652-2.027-.174-.294-.018-.454.13-.601.134-.132.296-.343.444-.515.147-.171.196-.294.296-.49.099-.196.05-.367-.024-.515-.074-.147-.659-1.568-.902-2.156-.237-.57-.478-.493-.659-.503-.171-.008-.366-.01-.561-.01-.196 0-.515.073-.784.367-.268.294-1.024 1.004-1.024 2.449 0 1.445 1.073 2.84 1.22 3.036.147.196 2.11 3.177 5.112 4.453.714.304 1.272.486 1.706.623.717.224 1.37.193 1.886.117.575-.085 1.748-.702 1.992-1.382.244-.68.244-1.263.171-1.382-.072-.12-.268-.196-.564-.343z"/>
                        </svg>
                        <span>Chat History</span>
                    </button>
                )}
            </div>

            {/* Lead Details Card */}
            <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-800 mb-6 pb-2 border-b">Lead Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
                    {[
                        { label: 'Name', value: leadData.lead_details.name || '-' },
                        { label: 'Phone Number', value: leadData.lead_details.phone || '-' },
                        { label: 'Email', value: leadData.lead_details.email || '-' },
                        { label: 'Lead Level', value: leadData.lead_details.current_status || '-' },
                        { label: 'Location', value: leadData.lead_details.location || '-' },
                        { label: 'Designation', value: leadData.lead_details.designation || '-' },
                        { label: 'Fresher/Experience', value: leadData.lead_details.fresher_experience || '-' },
                        { label: 'Experience', value: leadData.lead_details.experience !== null && leadData.lead_details.experience !== undefined ? `${leadData.lead_details.experience} Years` : '-' },
                        { label: 'Industries Worked', value: leadData.lead_details.industries_worked || '-' },
                        { label: 'Lead Source', value: leadData.lead_details.source || '-' },
                        { label: 'Expected CTC', value: leadData.lead_details.expected_ctc !== null && leadData.lead_details.expected_ctc !== undefined ? `${leadData.lead_details.expected_ctc} LPA` : '-' },
                        { label: 'Current CTC', value: leadData.lead_details.current_ctc !== null && leadData.lead_details.current_ctc !== undefined ? `${leadData.lead_details.current_ctc} LPA` : '-' },
                        { label: 'Date of Joining', value: leadData.lead_details.doj || '-' },
                        { label: 'Able to bring laptop', value: leadData.lead_details.have_laptop || '-' },
                        { label: 'Client Requirement', value: leadData.lead_details.requirement || '-' },
                        { label: 'Activity Category', value: leadData.lead_details.activity_category || '-' },
                        { label: 'Employee name', value: leadData.lead_details.employee_name || '-' },
                        { label: 'Assigned By', value: leadData.lead_details.assigned_by || '-' },
                        { label: 'Added on', value: leadData.lead_details.added_on || '-' },
                        { label: 'Message Sent to Candidate', value: leadData.lead_details.message_to_candidates || '-', isFullWidth: true },
                        { label: 'Remarks', value: leadData.lead_details.interview_call_remarks || '-', isFullWidth: true },
                    ].map((item, index) => (
                        <div key={index} className={`${item.isFullWidth ? "col-span-1 md:col-span-2" : "col-span-1"} flex items-start text-base py-1`}>
                            <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">{item.label} :</span>
                            <span className="text-gray-600 font-medium whitespace-pre-wrap ml-1">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Timeline */}
            <h3 className="text-xl font-bold text-gray-800 mb-4 px-1">History</h3>
            <div className="space-y-6">
                {leadData.history.map((log, index) => {
                    // Count positions of follow ups vs closures dynamically for headings
                    const isFollowUp = log.type === 'Follow Up' && !log.is_closing;
                    const isClosure = log.is_closing || log.display_status === 'Closed' || log.display_status === 'Rejected';
                    
                    const formattedDate = (d) => {
                        if (!d) return '-';
                        const dateObj = new Date(d);
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const year = dateObj.getFullYear();
                        return `${day}/${month}/${year}`;
                    };

                    const formattedDateTime = (d) => {
                        if (!d) return '-';
                        const dateObj = new Date(d);
                        const day = String(dateObj.getDate()).padStart(2, '0');
                        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                        const year = dateObj.getFullYear();
                        const timePart = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return `${day}/${month}/${year}, ${timePart}`;
                    };

                    if (isFollowUp) {
                        return (
                            <div key={log.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 transition-all hover:shadow-md">
                                <h5 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2 flex justify-between">
                                    <span>Follow Up</span>
                                    <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                                        Assigned To: {log.employee_name}
                                    </span>
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-base">
                                    <div className="flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Last Followup Date :</span>
                                        <span className="text-gray-600 font-medium ml-1">{formattedDate(log.last_fup_date)}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Expected Followup Date :</span>
                                        <span className="text-gray-600 font-medium ml-1">
                                            {log.expected_date ? `${formattedDate(log.expected_date)} ${formatTimeTo12Hour(log.expected_time)}` : '-'}
                                        </span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Followup Done Date :</span>
                                        <span className="text-gray-600 font-medium ml-1">{formattedDateTime(log.done_date)}</span>
                                    </div>
                                    <div className="col-span-1 md:col-span-2 flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Remarks :</span>
                                        <span className="text-gray-600 font-medium ml-1 whitespace-pre-wrap">{log.notes || 'No remarks recorded.'}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    if (isClosure) {
                        const isRejected = log.display_status === 'Rejected' || log.status === 'rejected';
                        const headingText = isRejected ? "Rejected" : "Closed";
                        const themeColor = isRejected ? "border-red-200 bg-red-50/30" : "border-gray-200 bg-gray-50/30";
                        const badgeColor = isRejected ? "text-red-700 bg-red-100" : "text-gray-700 bg-gray-100";
                        
                        return (
                            <div key={log.id} className={`rounded-2xl shadow-sm border p-6 transition-all hover:shadow-md ${themeColor}`}>
                                <h5 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2 flex justify-between">
                                    <span>{headingText}</span>
                                    <span className={`text-sm font-semibold px-3 py-1 rounded-full ${badgeColor}`}>
                                        Processed By: {log.employee_name}
                                    </span>
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-base">
                                    <div className="flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">{headingText} On :</span>
                                        <span className="text-gray-600 font-medium ml-1">{formattedDateTime(log.created_date || log.done_date)}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Stage Of closure :</span>
                                        <span className="text-gray-600 font-medium ml-1 capitalize">{String(log.stage_of_closure).replace(/_/g, ' ')}</span>
                                    </div>
                                    <div className="flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Reason for Closure :</span>
                                        <span className="text-gray-600 font-medium ml-1 capitalize">{String(log.closure_reason || '-').replace(/_/g, ' ')}</span>
                                    </div>
                                    <div className="col-span-1 md:col-span-2 flex items-start">
                                        <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Remarks :</span>
                                        <span className="text-gray-600 font-medium ml-1 whitespace-pre-wrap">{log.notes || 'No remarks recorded.'}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // Fallback representation for standard/general activity entries
                    return (
                        <div key={log.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 transition-all hover:shadow-md">
                            <h5 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2 flex justify-between">
                                <span>{log.display_status || log.activity_name}</span>
                                <span className="text-sm font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                                    By: {log.employee_name}
                                </span>
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-base">
                                <div className="flex items-start">
                                    <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Created On :</span>
                                    <span className="text-gray-600 font-medium ml-1">{formattedDateTime(log.created_date)}</span>
                                </div>
                                <div className="col-span-1 md:col-span-2 flex items-start">
                                    <span className="font-bold text-gray-800 min-w-[200px] flex-shrink-0">Remarks/Notes :</span>
                                    <span className="text-gray-600 font-medium ml-1 whitespace-pre-wrap">{log.notes || 'No notes recorded.'}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {leadData.history.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        No history found for this lead.
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadActivityLogPage;
